import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import type { OpcionDeFeedback } from "@/lib/feedback";

/**
 * Tope del comentario. Es el MISMO número que `StoreFeedbackRequest::MAX_TEXTO`
 * en el API: si se cambia uno hay que cambiar el otro, o la pantalla deja
 * escribir un texto que el servidor rechaza con un 422 recién al enviar.
 */
export const MAX_TEXTO = 4000;

const comentarioSchema = z.object({
  modulo: z.string().min(1, "Elige sobre qué módulo es el comentario."),
  tipo: z.string().min(1, "Indica si es un problema, una sugerencia o una consulta."),
  texto: z
    .string()
    .trim()
    .min(1, "Escribe tu comentario.")
    .max(MAX_TEXTO, `El comentario no puede superar los ${MAX_TEXTO} caracteres.`),
});

export type ComentarioValues = z.infer<typeof comentarioSchema>;

/** Deja pegar en el campo que corresponde el mensaje que devuelve un 422. */
export interface AccionesDelFormulario {
  errorDeCampo: (campo: keyof ComentarioValues, mensaje: string) => void;
  limpiar: () => void;
}

interface FormularioDeComentarioProps {
  modulos: OpcionDeFeedback[];
  tipos: OpcionDeFeedback[];
  /** Valores de partida. Sin ellos el formulario nace vacío (alta). */
  valorInicial?: ComentarioValues;
  /** Texto del botón que envía: "Enviar comentario" / "Guardar cambios". */
  etiquetaEnvio: string;
  enviando: boolean;
  onEnviar: (valores: ComentarioValues, acciones: AccionesDelFormulario) => void;
  onCancelar?: () => void;
}

const VACIO: ComentarioValues = { modulo: "", tipo: "", texto: "" };

/**
 * Los tres campos de un comentario: módulo, tipo y texto.
 *
 * Alta y edición comparten campos, validación y tratamiento del 422, así que
 * comparten componente; lo que cambia es el rótulo del botón y el submit.
 *
 * El TIPO no viene preseleccionado en el alta a propósito: es lo que decide
 * si algo se arregla, se agenda o se responde, y un valor por defecto se
 * envía solo y ensucia justo el dato que sirve para triar.
 */
export function FormularioDeComentario({
  modulos,
  tipos,
  valorInicial,
  etiquetaEnvio,
  enviando,
  onEnviar,
  onCancelar,
}: FormularioDeComentarioProps) {
  const form = useForm<ComentarioValues>({
    resolver: zodResolver(comentarioSchema),
    defaultValues: valorInicial ?? VACIO,
  });

  function handleSubmit(valores: ComentarioValues) {
    onEnviar(valores, {
      errorDeCampo: (campo, mensaje) =>
        form.setError(campo, { type: "server", message: mensaje }),
      limpiar: () => form.reset(VACIO),
    });
  }

  return (
    <Form {...form}>
      {/* `noValidate`: la validación NATIVA del navegador corre ANTES del
          submit y le gana a zod, dejando el formulario rechazando sin decir
          por qué dentro de la página (ver webapp/CLAUDE.md). */}
      <form
        noValidate
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-col gap-5"
      >
        <FormField
          control={form.control}
          name="modulo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Módulo</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Elige sobre qué parte del sistema es" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {modulos.map((modulo) => (
                    <SelectItem key={modulo.valor} value={modulo.valor}>
                      {modulo.etiqueta}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tipo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo</FormLabel>
              <FormControl>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="gap-3"
                >
                  {tipos.map((tipo) => (
                    <Label key={tipo.valor} className="text-sm font-medium">
                      <RadioGroupItem value={tipo.valor} /> {tipo.etiqueta}
                    </Label>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="texto"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Comentario</FormLabel>
              <FormControl>
                {/* `min-h-72` (288px) sobre el `min-h-20` del DS: el campo
                    chico invitaba a escribir una línea, y una línea no alcanza
                    para reproducir un problema. El `field-sizing-content` del
                    componente lo sigue agrandando solo al escribir. */}
                <Textarea
                  placeholder="Cuenta qué pasó, en qué pantalla y qué esperabas que pasara. Mientras más concreto, más rápido se resuelve."
                  className="min-h-72"
                  {...field}
                />
              </FormControl>
              <div className="flex items-start justify-between gap-3">
                <FormMessage />
                {/* El contador existe porque el límite existe: sin él, un
                    texto largo se rechaza recién al enviar, cuando ya está
                    escrito.

                    Se lee de `field.value` y NO de `form.watch`: el watch a
                    nivel de página re-renderizaría TODO —el tablero de
                    comentarios incluido— en cada tecla. El `Controller` de
                    este campo ya se re-renderiza solo cuando cambia este
                    valor. */}
                <span
                  className={
                    field.value.length > MAX_TEXTO
                      ? "text-destructive ml-auto shrink-0 text-xs font-medium"
                      : "text-ink-hint ml-auto shrink-0 text-xs"
                  }
                >
                  {field.value.length} / {MAX_TEXTO}
                </span>
              </div>
            </FormItem>
          )}
        />

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={enviando} className="gap-2">
            {enviando && <Spinner className="text-primary-foreground" />}
            {etiquetaEnvio}
          </Button>
          {onCancelar && (
            <Button type="button" variant="outline" onClick={onCancelar} disabled={enviando}>
              Cancelar
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
}
